import { useCallback, useMemo, useState } from 'react';
import { Admin, Resource, combineDataProviders } from 'react-admin';
import { BrowserRouter } from 'react-router-dom';

import queryClient from '@app/config/clients/query';
import authProvider from '@app/config/providers/auth';
import cmsApiDataProvider from '@app/config/providers/cmsApiData';
import theme from '@app/config/theme/default';

// Modules
import { CompanyResourceConfig } from '@app/pages/Companies';
import { PrmCompanyResourceConfig } from '@app/pages/PrmCompanies';
import { ExpertsResourceConfig } from '@app/pages/Experts';
import LoginPage from '@app/pages/LoginPage';
import { MediaResourceConfig } from '@app/pages/Media';

import MainLayout from '@app/layouts/MainLayout';

import { FullScreenLoading } from './components/FullScreenLoading';
import { AppContext } from './contexts';
import { CTAResourceConfig } from './pages/CTA';
import DashboardPage from './pages/DashboardPage';
import { EPGResourceConfig } from './pages/EPG';
import { EventsResourceConfig } from './pages/Events';
import { QuestionsResourceConfig } from './pages/Questions';
import { RailsResourceConfig } from './pages/Rails';
import { SeriesResourceConfig } from './pages/Series';
import { TaxonomiesResourceConfig } from './pages/Taxonomies';
import { UsersResourceConfig } from './pages/Users';

const dataProvider = combineDataProviders((resource) => {
  switch (resource) {
    case 'companies':
    case 'experts':
    case 'industries':
    case 'chixsymbols':
    case 'users/roles':
    case 'taxonomies':
    case 'media':
    case 'media/keywords':
    case 'series':
    case 'users':
    case 'rails':
    case 'categories':
    case 'events':
    case 'epgs':
    case 'ctas':
    case 'segments':
    case 'questions':
    case 'questions/responses':
      return cmsApiDataProvider;
    default:
      throw new Error(`Unknown resource: ${resource}`);
  }
});

const AdminConfig = {
  theme: theme,
  layout: MainLayout,
  // i18nProvider: i18nProvider,
  loginPage: LoginPage,
  dashboard: DashboardPage,
  queryClient: queryClient,
  authProvider: authProvider,
  dataProvider: dataProvider,
  requireAuth: true,
};

export default function App() {
  const [loading, setLoading] = useState(false);

  const handleChangeLoading = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  const provider = useMemo(() => {
    return { loading, handleChangeLoading };
  }, [loading, handleChangeLoading]);

  return (
    <AppContext.Provider value={provider}>
      <FullScreenLoading isLoading={loading} />
      <BrowserRouter>
        <Admin {...AdminConfig}>
          <Resource {...CompanyResourceConfig} />
          <Resource {...ExpertsResourceConfig} />
          <Resource {...UsersResourceConfig} />
          <Resource {...MediaResourceConfig} />
          <Resource {...SeriesResourceConfig} />
          <Resource {...RailsResourceConfig} />
          <Resource {...TaxonomiesResourceConfig} />
          <Resource {...CTAResourceConfig} />
          <Resource {...EventsResourceConfig} />
          <Resource {...EPGResourceConfig} />
          <Resource {...QuestionsResourceConfig} />
          <Resource {...PrmCompanyResourceConfig} />
        </Admin>
      </BrowserRouter>
    </AppContext.Provider>
  );
}
